import { useMemo } from 'react';
import { useUser } from 'contexts/user';
import { getTenantById } from '@tenants';
import { useConsentLabels } from 'hooks/use-consent-labels';
import { createConsentService } from 'services/consent-service';
import { useTranslation } from 'next-i18next';
import { Namespaces, LOCALE_COMMON_PATHS } from 'types/locales';
import { useTenantId } from './use-tenant-id';
import type { ConsentCaptureData, ConsentValidation } from 'services/consent-service';
import type { ThemeNames } from '@tenants/types';

export type ConsentServiceFacade = {
  isRequired: boolean;
  validate: (hasConsented: boolean) => ConsentValidation;
  capture: (data: Omit<ConsentCaptureData, 'source'>) => Promise<void>;
};

/**
 * Provides a consent facade and i18n labels for any form.
 *
 * `service.isRequired`   — whether to render consent checkboxes for this user.
 * `service.validate`     — call before submit; returns `{ ok: true }` when consent is satisfied.
 * `service.capture`      — call on successful submit; no-ops when consent is not required.
 *
 * `source` and `disclaimerText` are bound internally — consumers pass only user data.
 */
export function useConsentService(buttonText?: string): {
  service: ConsentServiceFacade;
  labels: {
    privacyPolicy: React.JSX.Element;
    marketingCommunications: string;
  };
} {
  const tenantId = useTenantId();
  const tenant = getTenantById(tenantId);
  const { userLocation } = useUser();
  const { t } = useTranslation(Namespaces.COMMON);
  const resolvedButtonText = buttonText ?? t(LOCALE_COMMON_PATHS.BUTTON_CONTINUE, { defaultValue: 'Continue' });
  const { fubConsentDisclaimerText, privacyPolicyConsentLabel, marketingCommunicationsConsentLabel } =
    useConsentLabels(resolvedButtonText);

  const consentService = useMemo(
    () => createConsentService(tenant.consent, userLocation),
    [tenantId, userLocation],
  );

  const service: ConsentServiceFacade = useMemo(
    () => ({
      isRequired: consentService.isRequired,
      validate: (hasConsented: boolean) => consentService.validate(hasConsented),
      capture: (data: Omit<ConsentCaptureData, 'source'>) =>
        consentService.capture({ ...data, source: tenant.theme.id as ThemeNames }, fubConsentDisclaimerText),
    }),
    [consentService, tenant.theme.id, fubConsentDisclaimerText],
  );

  return {
    service,
    labels: {
      privacyPolicy: privacyPolicyConsentLabel,
      marketingCommunications: marketingCommunicationsConsentLabel,
    },
  };
}
